import { SettingsRoleEnum } from './settings-role.enum';

export interface Settings {
  role: SettingsRoleEnum;
  name: string;
  codigosAnf: string;
  accessLevel: number;
  bClientStatus: boolean;
  /** True when the user can manage precos: `bidsweb:prices` on a login-farmacias session, the
   *  configured PrecosAdmin:AllowedUserProfileIds allowlist on a legacy one. */
  priceManager?: boolean;
  /** The pharmacies whose prices the user manages, `;`-separated: on a login-farmacias session those whose
   *  entry carries `bidsweb:prices` (grant-visibility G7). Read through `priceAnfCodes`. */
  priceCodigosAnf?: string;
  /** True when the user's ANF code is in Pim:AllowedAnfCodes. */
  pimAccess?: boolean;
  /** The pharmacies whose PIM this app offers, `;`-separated: all of them with `pimAccess`, otherwise those
   *  whose entry carries the role `dev` (G7). Empty means no PIM. Read through `pimAnfCodes`. */
  pimCodigosAnf?: string;
  /** The API's payments verdict: `bidsweb:operational_payments`, or `bidsops:payments` (the Ops application's,
   *  which this application does not honour), or Payments:AllowedEmails on a legacy session. This app reads
   *  it only for a legacy session; on SSO the guard and sidebar check the token for its own grant. */
  paymentsAccess?: boolean;
  /** True when the user may open this application's operational area: `bidsweb:operational` on a
   *  login-farmacias session, an internal address on a legacy one. Not the Bidscircle Ops
   *  application, which is a different project. Display only; the API enforces it. */
  operationalAccess?: boolean;
  /** The user's main pharmacy: on a login-farmacias session the token's `main_anf` (when that pharmacy
   *  carries `bidsweb:access`), on a legacy one the profile's CodigoAnf. The pharmacy a picker opens on;
   *  see `defaultAnf`. */
  codigoAnfPrincipal?: number;
  /** The user's employee ID from the profile. */
  employeeId?: number;
  /** Editable ANF-extra codes. */
  editableAnfExtraCodes?: number[];
  /** Locked ANF-extra codes that must be preserved. */
  lockedAnfExtraCodes?: number[];
}

/**
 * The pharmacy a picker opens on: `codigoAnfPrincipal` when it is one of the caller's `codes`, else the
 * first listed, as before there was a principal to follow. Keyed on the stated field rather than on
 * `codigosAnf`'s order, which happens to put the principal first today and is not promised to.
 */
export function defaultAnf<T extends string | number>(codes: T[], principal: number | null | undefined): T | undefined {
  if (principal != null) {
    const hit = codes.find((c) => Number(c) === principal);
    if (hit !== undefined) return hit;
  }
  return codes[0];
}

/** A `;`-separated list of ANFs, as the settings carry them, as numbers. */
export function parseAnfCodes(codes: string | null | undefined): number[] {
  return (codes ?? '')
    .split(';')
    .map((c) => parseInt(c.trim(), 10))
    .filter((n) => !isNaN(n) && n > 0);
}

/**
 * The pharmacies the price page offers: `priceCodigosAnf`, read per pharmacy (G7). An API that does not
 * send the field yet leaves the old answer, every pharmacy of `codigosAnf`.
 */
export function priceAnfCodes(settings: Settings | null | undefined): number[] {
  if (!settings) return [];
  return parseAnfCodes(settings.priceCodigosAnf ?? settings.codigosAnf);
}

/**
 * The pharmacies PIM is offered for: `pimCodigosAnf`, where the role `dev` counts only at the pharmacy
 * that holds it (G7). An API that does not send the field yet leaves the old rule: every pharmacy of
 * `codigosAnf` for the `dev` role or `pimAccess`, none otherwise.
 */
export function pimAnfCodes(settings: Settings | null | undefined): number[] {
  if (!settings) return [];
  if (settings.pimCodigosAnf != null) return parseAnfCodes(settings.pimCodigosAnf);
  return settings.role === SettingsRoleEnum.dev || settings.pimAccess ? parseAnfCodes(settings.codigosAnf) : [];
}
